// Admin "Mail delivery" page (flag REACT_APP_MAIL_DELIVERY_ADMIN_ENABLED,
// VELKOMSTMAIL-RENDER-FEJL-2026-10-01.md §3). The wire of
// GET ops/mail-delivery (API flag MailDeliveryAdminListEnabled - 404 while
// it is off) and POST ops/users/welcome-mail/resend-batch (API flag
// WelcomeMailResendBatchEnabled - 404 while it is off). The repository
// normalises both answers, so the screen never reads a raw body.

// What SendGrid did with one mail, as the API derives it from its events
// (dropped / bounce / spamreport = notDelivered). `sent` / `processed` =
// accepted, no final event yet. An unknown value is kept as it is and
// shown as text.
export type MailDeliveryStatus =
  | "sent"
  | "processed"
  | "deferred"
  | "delivered"
  | "opened"
  | "notDelivered";

export const MAIL_DELIVERY_DAYS = [1, 3, 7, 30] as const;
export type MailDeliveryDays = (typeof MAIL_DELIVERY_DAYS)[number];

// The API's status filter. Each value matches the row status EXACTLY
// (delivered = delivered and NOT opened - an opened mail is "opened");
// pending = sent + processed (accepted by SendGrid, no verdict yet).
export const MAIL_DELIVERY_STATUS_FILTERS = [
  "all",
  "notDelivered",
  "delivered",
  "deferred",
  "opened",
  "pending",
] as const;
export type MailDeliveryStatusFilter =
  (typeof MAIL_DELIVERY_STATUS_FILTERS)[number];

// The rows one GET may carry (the contract's `take`); a list of exactly
// this many rows is said to be cut.
export const MAIL_DELIVERY_TAKE = 200;

// The welcome list (kind=welcome = first sends + resends, every status):
// what "who still lacks the welcome mail" is read off - per user the LATEST
// welcome mail decides. Read with the API's own cap (MailDeliveryPolicy.
// MaxTake) so mails of other kinds never push a welcome mail out of the
// answer and only a period with more welcome mails than this is cut.
export const MAIL_DELIVERY_WELCOME_TAKE = 1000;

export interface MailDeliveryQuery {
  days: MailDeliveryDays;
  status: MailDeliveryStatusFilter;
  // "all", "welcome" or any other kind the API knows.
  kind: string;
  orgId?: number;
  // Rows asked for; MAIL_DELIVERY_TAKE when left out.
  take?: number;
}

export function welcomeListQuery(days: MailDeliveryDays): MailDeliveryQuery {
  return {
    days,
    status: "all",
    kind: "welcome",
    take: MAIL_DELIVERY_WELCOME_TAKE,
  };
}

export interface MailDeliveryItemDto {
  messageId: string;
  kind: string;
  userId: number | null;
  organizationId: number | null;
  organizationName: string | null;
  recipient: string;
  status: string;
  reason: string | null;
  sentAtUtc: string | null;
  lastEventAtUtc: string | null;
  // The API's verdict: a welcome mail whose user exists and has never
  // logged in. Only `true` offers "Resend welcome mail".
  canResendWelcome: boolean;
}

export interface MailDeliverySummaryDto {
  total: number;
  delivered: number;
  notDelivered: number;
  deferred: number;
  opened: number;
  pending: number;
}

export interface MailDeliveryListDto {
  items: MailDeliveryItemDto[];
  summary: MailDeliverySummaryDto;
}

// Body of POST ops/users/welcome-mail/resend-batch. dryRun is ALWAYS sent
// (the API defaults it to true; only an explicit false resets passwords and
// sends). At most 50 ids per call - the repository chunks.
export interface WelcomeMailResendBatchRequest {
  userIds: number[];
  dryRun: boolean;
  ccPrimaryContact: boolean;
}

export interface WelcomeMailResendBatchItemDto {
  userId: number;
  orgId: number | null;
  // sent | wouldSend | alreadyLoggedIn | noLoginAccount | throttled |
  // excludedRecipient | notFound | orgNotFound | wrongOrganization |
  // notCustomer | graphFailed | mailFailed | error - or "notRun" (set by the
  // repository for ids whose call never got an answer).
  verdict: string;
  welcomeMailSentUtc: string | null;
  welcomeMailSendCount: number | null;
  message: string | null;
}

export interface WelcomeMailResendBatchResultDto {
  dryRun: boolean;
  requested: number;
  run: number;
  totals: Record<string, number>;
  results: WelcomeMailResendBatchItemDto[];
}
